<template>
    <div class="entity-container">
        <div class="entity-header">
            <Title>{{ title }}</Title>
            <Button
                v-if="isEditing && showAddButton"
                class="p-button-secondary"
                icon="pi pi-plus"
                :label="addButtonLabel"
                severity="secondary"
                text
                @click="$emit('add')"
            />
        </div>
        <p v-if="description" class="section-description">{{ description }}</p>

        <div v-if="entities && entities.length > 0" class="entity-grid">
            <slot></slot>
        </div>

        <slot v-else name="empty">
            <div class="empty-state">
                <div class="empty-content">
                    <i :class="`pi ${emptyIcon} empty-icon`"></i>
                    <h3>{{ emptyTitle }}</h3>
                    <p>{{ emptyDescription }}</p>
                    <Button
                        v-if="isEditing && showAddButton"
                        :label="addButtonLabel"
                        icon="pi pi-plus"
                        severity="primary"
                        @click="$emit('add')"
                    />
                </div>
            </div>
        </slot>
    </div>
</template>

<script setup lang="ts">
import Button from "primevue/button";

import Title from "@/components/Title.vue";

withDefaults(
    defineProps<{
        title: string;
        description?: string;
        entities: { identity: string }[];
        isEditing?: boolean;
        showAddButton?: boolean;
        addButtonLabel?: string;
        addEntityLabel?: string;
        addEntityIcon?: string;
        emptyIcon?: string;
        emptyTitle?: string;
        emptyDescription?: string;
    }>(),
    {
        isEditing: false,
        showAddButton: true,
        addButtonLabel: "Add",
        addEntityLabel: "Add Item",
        addEntityIcon: "pi-plus",
        emptyIcon: "pi-folder-open",
        emptyTitle: "No items yet",
        emptyDescription: "Create your first item to get started",
    }
);

defineEmits<{
    (e: "add"): void;
}>();
</script>

<style lang="scss" scoped>
.entity-container {
    width: 100%;
}

.entity-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0;
}

.section-description {
    margin: 0.25rem 0 1.5rem;
    color: var(--p-text-muted-color);
    font-size: 0.875rem;
    font-style: italic;
}

.entity-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1.5rem;
}

.empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px dashed var(--surface-border);
    border-radius: var(--border-radius);
    margin-top: 2rem;

    .empty-content {
        text-align: center;

        .empty-icon {
            font-size: 3rem;
            color: var(--text-color);
            margin-bottom: 1rem;
            display: block;
        }

        h3 {
            margin: 0 0 0.5rem 0;
            color: var(--text-color);
        }

        p:last-of-type {
            margin: 0 0 1rem 0;
            color: var(--text-color);
        }
    }
}
</style>
